import "../css/subpage.less";
import React from "react";
import LocalizedLink from "../components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { getImageUrl } from "../config";
import PageMeta from "../common/PageMeta";
import SubpageTitle from "../common/SubpageTitle";
import FaqStickyLink from "../common/FaqStickyLink";
import { PAGE_META } from "../seo/pageMeta";

const Service: React.FC = () => {
  const { t } = useTranslation();

  const serviceSoftwareCards = [
    {
      image: "/image/img_service_biz01.png",
      alt: t("service_page.card1_title"),
      title: t("service_page.card1_title"),
      description: t("service_page.card1_desc"),
    },
    {
      image: "/image/img_service_biz02.png",
      alt: t("service_page.card2_title"),
      title: t("service_page.card2_title"),
      description: t("service_page.card2_desc"),
    },
    {
      image: "/image/img_service_biz03.png",
      alt: t("service_page.card3_title"),
      title: t("service_page.card3_title"),
      description: t("service_page.card3_desc"),
    },
  ];

  const serviceAchievementCol1 = [
    t("service_page.ach_1"),
    t("service_page.ach_2"),
    t("service_page.ach_3"),
    t("service_page.ach_4"),
  ];

  const serviceAchievementCol2 = [
    t("service_page.ach_5"),
    t("service_page.ach_6"),
    t("service_page.ach_7"),
    t("service_page.ach_8"),
  ];

  const serviceStrengthItems = [
    {
      image: "/image/img_service_strength01.png",
      alt: t("service_page.st1_line1"),
      line1: t("service_page.st1_line1"),
      line2: t("service_page.st1_line2"),
      body: t("service_page.st1_body"),
    },
    {
      image: "/image/img_service_strength02.png",
      alt: t("service_page.st2_line1"),
      line1: t("service_page.st2_line1"),
      line2: t("service_page.st2_line2"),
      body: t("service_page.st2_body"),
    },
    {
      image: "/image/img_service_strength03.png",
      alt: t("service_page.st3_line1"),
      line1: t("service_page.st3_line1"),
      line2: t("service_page.st3_line2"),
      body: t("service_page.st3_body"),
    },
  ];

  return (
    <div className="subpage subpage--service">
      <PageMeta path="/service" {...PAGE_META["/service"]} />
      <section className="subpage-section service-mv" aria-label="サービス紹介">

        <SubpageTitle titleJa="サービス" titleEn="service" as="h1" />
        <FaqStickyLink />
        <div className="service-mv__catchphrase">
          <p className="service-mv__catchphrase-text">
            {t("service_page.catchphrase")}
          </p>
          <span className="service-mv__catchphrase-line" aria-hidden />
        </div>
        <div className="service-mv__figure">
          <img
            src={getImageUrl("/image/img_service_mv.png")}
            alt=""
          />
        </div>
        <p className="service-mv__description">
          {t("service_page.description")}
        </p>
      </section>

      <section
        className="subpage-section service-strength"
        aria-labelledby="service-strength-heading"
      >
        <h2 id="service-strength-heading" className="service-strength__banner">
          {t("service_page.banner_title")}
        </h2>
        <div className="service-strength__inner">
          {serviceStrengthItems.map((item) => (
            <article key={item.image} className="service-strength__row">
              <div className="service-strength__media">
                <img src={getImageUrl(item.image)} alt={item.alt} />
              </div>
              <div className="service-strength__body">
                <h3 className="service-strength__heading">
                  <span className="service-strength__heading-line1">
                    {item.line1}
                  </span>
                  <span className="service-strength__heading-line2">
                    {item.line2}
                  </span>
                </h3>
                <p className="service-strength__text">{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className="subpage-section service-business"
        aria-labelledby="service-business-software-heading"
      >
        <div className="service-business__inner">
          <div className="service-business__block">
            <h2
              id="service-business-software-heading"
              className="service-business__section-title"
            >
              <span
                className="service-business__section-title-bar"
                aria-hidden
              />
              <span className="service-business__section-title-text">
                {t("service_page.sec_software")}
              </span>
            </h2>
            <p className="service-business__lead">
              {t("service_page.lead_software")}
            </p>

            <div className="service-business__grid3">
              {serviceSoftwareCards.map((card) => (
                <article key={card.image} className="service-business__card">
                  <div className="service-business__card-img">
                    <img
                      src={getImageUrl(card.image)}
                      alt={card.alt}
                    />
                  </div>
                  <h3 className="service-business__card-title">
                    {card.title}
                  </h3>
                  <p className="service-business__card-text">
                    {card.description}
                  </p>
                </article>
              ))}
            </div>

            <div className="service-business__achievements">
              <h3 className="service-business__achievements-title">
                {t("service_page.achievements_title")}
              </h3>
              <div className="service-business__achievements-cols">
                <ul className="service-business__achievements-list">
                  {serviceAchievementCol1.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <ul className="service-business__achievements-list">
                  {serviceAchievementCol2.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="service-business__cta-wrap">
              <LocalizedLink
                to="/development"
                className="service-business__cta"
              >
                {t("service_page.view_achievements")}
              </LocalizedLink>
            </div>
          </div>

          <div className="service-business__lower">
            <div className="service-business__lower-col">
              <h2 className="service-business__section-title">
                <span
                  className="service-business__section-title-bar"
                  aria-hidden
                />
                <span className="service-business__section-title-text">
                  {t("service_page.sec_edu")}
                </span>
              </h2>
              <div className="service-business__lower-media">
                <img
                  src={getImageUrl("/image/img_service_biz04.png")}
                  alt=""
                />
              </div>
              <p className="service-business__lower-text">
                {t("service_page.edu_text")}
              </p>
              <p className="service-business__lower-subhead">{t("service_page.edu_subhead")}</p>
              <ul className="service-business__lower-list">
                <li>{t("service_page.edu_1")}</li>
                <li>{t("service_page.edu_2")}</li>
              </ul>
            </div>

            <div className="service-business__lower-col">
              <h2 className="service-business__section-title">
                <span
                  className="service-business__section-title-bar"
                  aria-hidden
                />
                <span className="service-business__section-title-text">
                  {t("service_page.sec_sales")}
                </span>
              </h2>
              <div className="service-business__lower-media">
                <img
                  src={getImageUrl("/image/img_service_biz05.png")}
                  alt=""
                />
              </div>
              <p className="service-business__lower-text">
                {t("service_page.sales_text")}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Service;
